export const COGO_DOCUMENT_TEMPLATE_FAMILY_SCHEMA_VERSION =
  "cogo.document-template-family.v2" as const;

export const COGO_DOCUMENT_TEMPLATE_LAYOUT_PROFILES = [
  "flow-page",
  "fixed-page",
  "workbook",
  "block-page",
] as const;

export const COGO_DOCUMENT_TEMPLATE_FAMILY_RENDER_TARGETS = [
  "docx",
  "hwpx",
  "pptx",
  "xlsx",
  "pdf",
] as const;

export const COGO_DOCUMENT_TEMPLATE_FAMILY_PUBLICATION_TARGETS = [
  "google-docs",
  "notion",
] as const;

export type CogoDocumentTemplateLayoutProfile =
  (typeof COGO_DOCUMENT_TEMPLATE_LAYOUT_PROFILES)[number];
export type CogoDocumentTemplateFamilyRenderTarget =
  (typeof COGO_DOCUMENT_TEMPLATE_FAMILY_RENDER_TARGETS)[number];
export type CogoDocumentTemplateFamilyPublicationTarget =
  (typeof COGO_DOCUMENT_TEMPLATE_FAMILY_PUBLICATION_TARGETS)[number];
export type CogoDocumentTemplateFamilyTarget =
  | CogoDocumentTemplateFamilyRenderTarget
  | CogoDocumentTemplateFamilyPublicationTarget;
export type CogoDocumentTemplateCapabilityStatus =
  | "production-ready"
  | "partial"
  | "planned";

export const COGO_DOCUMENT_TEMPLATE_PROFILE_TARGET_COMPATIBILITY = {
  "flow-page": {
    renderTargets: ["docx", "hwpx", "pdf"],
    publicationTargets: ["google-docs"],
  },
  "fixed-page": {
    renderTargets: ["pptx", "pdf"],
    publicationTargets: [],
  },
  workbook: {
    renderTargets: ["xlsx", "pdf"],
    publicationTargets: [],
  },
  "block-page": {
    renderTargets: [],
    publicationTargets: ["notion"],
  },
} as const satisfies Record<
  CogoDocumentTemplateLayoutProfile,
  {
    renderTargets: readonly CogoDocumentTemplateFamilyRenderTarget[];
    publicationTargets: readonly CogoDocumentTemplateFamilyPublicationTarget[];
  }
>;

export interface CogoDocumentTemplateFamilyLegacyRef {
  schemaVersion: "cogo.document-template.v1";
  templateId: string;
  templateVersion: string;
  manifestRef: string;
  manifestSha256: string;
}

export interface CogoDocumentTemplateReferencedSlot {
  id: string;
  slotRef: string;
  kind: CogoDocumentTemplateSemanticSlotKind;
  required: boolean;
  semanticRole: string;
}

export type CogoDocumentTemplateSemanticSlotKind =
  | "scalar"
  | "rich-text"
  | "table"
  | "image"
  | "chart"
  | "repeated-group"
  | "formula-input"
  | "provider-property";

export interface CogoDocumentTemplateInlineSlot {
  id: string;
  kind: CogoDocumentTemplateSemanticSlotKind;
  bindingPath: string;
  required: boolean;
  semanticRole: string;
  shapeRef?: string;
}

export type CogoDocumentTemplateFamilySlot =
  | CogoDocumentTemplateReferencedSlot
  | CogoDocumentTemplateInlineSlot;

export interface CogoDocumentTemplateFamilySemanticCore {
  dataSchema: { ref: string; sha256?: string };
  slots: CogoDocumentTemplateFamilySlot[];
  tokens: { designTokenRefs: string[] };
  assets: Array<{
    id: string;
    role: string;
    sourceRef: string;
    mediaType: string;
    sha256?: string;
  }>;
  guidelineRefs: Array<{ ref: string; required: boolean; sha256?: string }>;
  llmPolicy: {
    allowedRoles: Array<
      | "semantic-input-assistant"
      | "layout-proposal-assistant"
      | "content-revision-assistant"
    >;
    canonicalAuthority: false;
    finalWriterAllowed: false;
    outputRequiresValidation: true;
  };
  actionFlowRefs: Array<{
    id: string;
    ref: string;
    role:
      | "generation"
      | "approval"
      | "refresh"
      | "export"
      | "publication"
      | "interaction";
    sha256?: string;
  }>;
  proofProfile: {
    requiredForPromotion: true;
    nativeStructureReadbackRequired: true;
    targetReadbackRequired: true;
    structureHashRequired: true;
    providerVersionPolicy: "required-when-provider";
    visualDiffPolicy: "always" | "when-layout-bearing";
    evidenceRetention: "immutable";
  };
  sourceProvenance: {
    required: true;
    sourceRefs: string[];
    preserveProviderVersion: true;
    sourceAdaptersCanonical: false;
  };
}

interface CogoDocumentTemplateTargetOverlayBase {
  target: CogoDocumentTemplateFamilyTarget;
  adapter: string;
  canonicalAuthority: false;
  implementationStatus: CogoDocumentTemplateCapabilityStatus;
  readbackRequired: true;
}

export interface CogoDocumentTemplateDocxOverlay
  extends CogoDocumentTemplateTargetOverlayBase {
  target: "docx";
  templateMode: "generated-from-cogo" | "optional-native-template";
}

export interface CogoDocumentTemplateHwpxOverlay
  extends CogoDocumentTemplateTargetOverlayBase {
  target: "hwpx";
  templateMode: "generated-from-cogo" | "optional-native-template";
}

export interface CogoDocumentTemplatePptxOverlay
  extends CogoDocumentTemplateTargetOverlayBase {
  target: "pptx";
  capabilityMode: "static-only";
  motionOverlayStatus: "not-implemented";
  sourceTemplateRef?: string;
}

export interface CogoDocumentTemplateXlsxOverlay
  extends CogoDocumentTemplateTargetOverlayBase {
  target: "xlsx";
  sourceTemplateRequired: true;
  sourceTemplateRef: string;
  workbookSidecarRef: string;
}

export interface CogoDocumentTemplatePdfOverlay
  extends CogoDocumentTemplateTargetOverlayBase {
  target: "pdf";
  outputOnly: true;
  editable: false;
}

export interface CogoDocumentTemplateGoogleDocsOverlay
  extends CogoDocumentTemplateTargetOverlayBase {
  target: "google-docs";
  templateMode: "native-copy";
  sourceTemplateRef: string;
  copyBeforeMutation: true;
}

export interface CogoDocumentTemplateNotionCogoBlockOverlay
  extends CogoDocumentTemplateTargetOverlayBase {
  target: "notion";
  templateMode: "cogo-block-template";
  blockTemplateRef: string;
  asyncTemplateReadbackRequired: false;
}

export interface CogoDocumentTemplateNotionDataSourceOverlay
  extends CogoDocumentTemplateTargetOverlayBase {
  target: "notion";
  templateMode: "data-source-template";
  templateSelection: "explicit-template" | "default-template";
  templateRef?: string;
  asyncTemplateReadbackRequired: true;
}

export type CogoDocumentTemplateTargetOverlay =
  | CogoDocumentTemplateDocxOverlay
  | CogoDocumentTemplateHwpxOverlay
  | CogoDocumentTemplatePptxOverlay
  | CogoDocumentTemplateXlsxOverlay
  | CogoDocumentTemplatePdfOverlay
  | CogoDocumentTemplateGoogleDocsOverlay
  | CogoDocumentTemplateNotionCogoBlockOverlay
  | CogoDocumentTemplateNotionDataSourceOverlay;

interface CogoDocumentTemplateFamilyVariantBase {
  variantId: string;
  name: string;
  capabilityStatus: CogoDocumentTemplateCapabilityStatus;
  knownLimitations: string[];
  sourcePolicy: {
    canonicalModel: "cogo-document-template-family";
    editorAdapters: Array<
      | "wordgard"
      | "cogo-fixed-page-editor"
      | "univer"
      | "cogo-block-editor"
      | "openpencil"
      | "google-docs"
      | "notion"
    >;
    adapterCanonicalAuthority: false;
  };
  slotBindings: Array<{ slotId: string; bindingRef: string }>;
  approvedOmissions: Array<{
    slotId: string;
    reason: string;
    approvedByRef: string;
    evidenceRef: string;
  }>;
  targetOverlays: CogoDocumentTemplateTargetOverlay[];
}

export type CogoDocumentTemplateFamilyVariant =
  | (CogoDocumentTemplateFamilyVariantBase & {
      layoutProfile: "flow-page";
      layout: {
        kind: "flow-page";
        layoutRef: string;
        layoutSha256?: string;
        pagination: "flow";
      };
      renderTargets: Array<"docx" | "hwpx" | "pdf">;
      publicationTargets: Array<"google-docs">;
    })
  | (CogoDocumentTemplateFamilyVariantBase & {
      layoutProfile: "fixed-page";
      layout: {
        kind: "fixed-page";
        masterRef: string;
        canvas: "wide" | "standard" | "a4" | "letter";
      };
      renderTargets: Array<"pptx" | "pdf">;
      publicationTargets: [];
    })
  | (CogoDocumentTemplateFamilyVariantBase & {
      layoutProfile: "workbook";
      layout: { kind: "workbook"; workbookSidecarRef: string };
      renderTargets: Array<"xlsx" | "pdf">;
      publicationTargets: [];
    })
  | (CogoDocumentTemplateFamilyVariantBase & {
      layoutProfile: "block-page";
      layout: { kind: "block-page"; blockSchemaRef: string };
      renderTargets: [];
      publicationTargets: ["notion"];
    });

export interface CogoDocumentTemplateFamily {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_FAMILY_SCHEMA_VERSION;
  familyId: string;
  familyVersion: string;
  name: string;
  description: string;
  metadata: { ownerRef: string; locale: string; tags: string[] };
  legacyTemplateRefs: CogoDocumentTemplateFamilyLegacyRef[];
  semanticCore: CogoDocumentTemplateFamilySemanticCore;
  promotionPolicy: {
    humanPromotionRequired: true;
    automatedPromotionAllowed: false;
    modelMayApprove: false;
    evidenceRequired: true;
  };
  variants: CogoDocumentTemplateFamilyVariant[];
}

const SEMANTIC_SLOT_KINDS = [
  "scalar",
  "rich-text",
  "table",
  "image",
  "chart",
  "repeated-group",
  "formula-input",
  "provider-property",
] as const satisfies readonly CogoDocumentTemplateSemanticSlotKind[];

const LLM_ROLES = [
  "semantic-input-assistant",
  "layout-proposal-assistant",
  "content-revision-assistant",
] as const;

const ACTION_FLOW_ROLES = [
  "generation",
  "approval",
  "refresh",
  "export",
  "publication",
  "interaction",
] as const;

const EDITOR_ADAPTERS = [
  "wordgard",
  "cogo-fixed-page-editor",
  "univer",
  "cogo-block-editor",
  "openpencil",
  "google-docs",
  "notion",
] as const;

const CAPABILITY_STATUSES = [
  "production-ready",
  "partial",
  "planned",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isKebabId(value: unknown): value is string {
  return typeof value === "string" && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}

function isUnique(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

function isSha256(value: unknown): value is string {
  return typeof value === "string" && /^sha256:[a-f0-9]{64}$/.test(value);
}

function validateOnlyKeys(
  errors: string[],
  value: Record<string, unknown>,
  allowedKeys: readonly string[],
  path: string,
): void {
  const unexpected = Object.keys(value).filter(
    (key) => !allowedKeys.includes(key),
  );
  if (unexpected.length > 0) {
    errors.push(`${path} has unsupported properties: ${unexpected.join(", ")}.`);
  }
}

function validateStringList(
  errors: string[],
  value: unknown,
  path: string,
  options: {
    minItems?: number;
    unique?: boolean;
    allowed?: readonly string[];
    kebab?: boolean;
  } = {},
): string[] {
  if (!isStringArray(value)) {
    errors.push(`${path} must be a string array.`);
    return [];
  }
  if (options.minItems !== undefined && value.length < options.minItems) {
    errors.push(`${path} requires at least ${options.minItems} item(s).`);
  }
  if (options.unique && !isUnique(value)) {
    errors.push(`${path} must be unique.`);
  }
  value.forEach((entry, index) => {
    if (!entry.trim()) errors.push(`${path}[${index}] must be non-empty.`);
    if (options.allowed && !options.allowed.includes(entry)) {
      errors.push(`${path}[${index}] is unsupported.`);
    }
    if (options.kebab && !isKebabId(entry)) {
      errors.push(`${path}[${index}] must be kebab-case.`);
    }
  });
  return value;
}

function validateOptionalSha256(
  errors: string[],
  value: unknown,
  path: string,
): void {
  if (value !== undefined && !isSha256(value)) {
    errors.push(`${path} must be SHA-256.`);
  }
}

function pushDuplicateIdError(
  errors: string[],
  values: unknown,
  field: string,
  label: string,
): void {
  if (!Array.isArray(values)) return;
  const ids = values
    .filter(isRecord)
    .map((entry) => entry[field])
    .filter((entry): entry is string => typeof entry === "string");
  if (!isUnique(ids)) errors.push(`${label} ids must be unique.`);
}

function validateRequiredPolicy(
  errors: string[],
  value: unknown,
  expected: Record<string, unknown>,
  label: string,
): void {
  if (!isRecord(value)) {
    errors.push(`${label} is required.`);
    return;
  }
  for (const [key, expectedValue] of Object.entries(expected)) {
    if (value[key] !== expectedValue) {
      errors.push(`${label}.${key} must be ${JSON.stringify(expectedValue)}.`);
    }
  }
}

function validateTargetOverlayPolicy(
  errors: string[],
  overlay: Record<string, unknown>,
  path: string,
): void {
  const target = overlay.target;
  const baseKeys = [
    "target",
    "adapter",
    "canonicalAuthority",
    "implementationStatus",
    "readbackRequired",
  ];
  if (!isNonEmptyString(overlay.adapter)) {
    errors.push(`${path}.adapter is required.`);
  }
  if (overlay.canonicalAuthority !== false) {
    errors.push(`${path}.canonicalAuthority must be false.`);
  }
  if (overlay.readbackRequired !== true) {
    errors.push(`${path}.readbackRequired must be true.`);
  }
  if (!CAPABILITY_STATUSES.includes(
    overlay.implementationStatus as CogoDocumentTemplateCapabilityStatus,
  )) {
    errors.push(`${path}.implementationStatus is unsupported.`);
  }

  switch (target) {
    case "docx":
    case "hwpx":
      validateOnlyKeys(errors, overlay, [...baseKeys, "templateMode"], path);
      if (
        overlay.templateMode !== "generated-from-cogo" &&
        overlay.templateMode !== "optional-native-template"
      ) {
        errors.push(`${path}.templateMode is unsupported for ${target}.`);
      }
      break;
    case "pptx":
      validateOnlyKeys(
        errors,
        overlay,
        [
          ...baseKeys,
          "capabilityMode",
          "motionOverlayStatus",
          "sourceTemplateRef",
        ],
        path,
      );
      if (overlay.capabilityMode !== "static-only") {
        errors.push(`${path}.capabilityMode must remain static-only.`);
      }
      if (overlay.motionOverlayStatus !== "not-implemented") {
        errors.push(
          `${path}.motionOverlayStatus must remain not-implemented until motion support ships.`,
        );
      }
      if (
        overlay.sourceTemplateRef !== undefined &&
        !isNonEmptyString(overlay.sourceTemplateRef)
      ) {
        errors.push(`${path}.sourceTemplateRef must be non-empty when present.`);
      }
      break;
    case "xlsx":
      validateOnlyKeys(
        errors,
        overlay,
        [
          ...baseKeys,
          "sourceTemplateRequired",
          "sourceTemplateRef",
          "workbookSidecarRef",
        ],
        path,
      );
      if (overlay.sourceTemplateRequired !== true) {
        errors.push(`${path}.sourceTemplateRequired must be true.`);
      }
      if (!isNonEmptyString(overlay.sourceTemplateRef)) {
        errors.push(`${path}.sourceTemplateRef is required.`);
      }
      if (!isNonEmptyString(overlay.workbookSidecarRef)) {
        errors.push(`${path}.workbookSidecarRef is required.`);
      }
      break;
    case "pdf":
      validateOnlyKeys(
        errors,
        overlay,
        [...baseKeys, "outputOnly", "editable"],
        path,
      );
      if (overlay.outputOnly !== true || overlay.editable !== false) {
        errors.push(`${path} must keep PDF output-only and non-editable.`);
      }
      break;
    case "google-docs":
      validateOnlyKeys(
        errors,
        overlay,
        [
          ...baseKeys,
          "templateMode",
          "sourceTemplateRef",
          "copyBeforeMutation",
        ],
        path,
      );
      if (overlay.templateMode !== "native-copy") {
        errors.push(`${path}.templateMode must be native-copy.`);
      }
      if (!isNonEmptyString(overlay.sourceTemplateRef)) {
        errors.push(`${path}.sourceTemplateRef is required.`);
      }
      if (overlay.copyBeforeMutation !== true) {
        errors.push(`${path}.copyBeforeMutation must be true.`);
      }
      break;
    case "notion":
      if (overlay.templateMode === "cogo-block-template") {
        validateOnlyKeys(
          errors,
          overlay,
          [
            ...baseKeys,
            "templateMode",
            "blockTemplateRef",
            "asyncTemplateReadbackRequired",
          ],
          path,
        );
        if (!isNonEmptyString(overlay.blockTemplateRef)) {
          errors.push(`${path}.blockTemplateRef is required.`);
        }
        if (overlay.asyncTemplateReadbackRequired !== false) {
          errors.push(
            `${path}.asyncTemplateReadbackRequired must be false for cogo-block-template.`,
          );
        }
      } else if (overlay.templateMode === "data-source-template") {
        validateOnlyKeys(
          errors,
          overlay,
          [
            ...baseKeys,
            "templateMode",
            "templateSelection",
            "templateRef",
            "asyncTemplateReadbackRequired",
          ],
          path,
        );
        if (
          overlay.templateSelection !== "explicit-template" &&
          overlay.templateSelection !== "default-template"
        ) {
          errors.push(`${path}.templateSelection is unsupported.`);
        }
        if (
          overlay.templateSelection === "explicit-template" &&
          !isNonEmptyString(overlay.templateRef)
        ) {
          errors.push(`${path}.templateRef is required for explicit-template.`);
        }
        if (
          overlay.templateSelection === "default-template" &&
          overlay.templateRef !== undefined
        ) {
          errors.push(`${path}.templateRef is forbidden for default-template.`);
        }
        if (overlay.asyncTemplateReadbackRequired !== true) {
          errors.push(
            `${path}.asyncTemplateReadbackRequired must be true for data-source-template.`,
          );
        }
      } else {
        errors.push(`${path}.templateMode is unsupported for Notion.`);
      }
      break;
    default:
      errors.push(`${path}.target is unsupported.`);
  }
}

function validateVariantLayout(
  errors: string[],
  value: unknown,
  profile: CogoDocumentTemplateLayoutProfile,
  path: string,
): void {
  if (!isRecord(value)) {
    errors.push(`${path} is required.`);
    return;
  }
  if (value.kind !== profile) {
    errors.push(`${path}.kind must match layoutProfile ${profile}.`);
  }
  switch (profile) {
    case "flow-page":
      validateOnlyKeys(
        errors,
        value,
        ["kind", "layoutRef", "layoutSha256", "pagination"],
        path,
      );
      if (!isNonEmptyString(value.layoutRef)) errors.push(`${path}.layoutRef is required.`);
      if (value.layoutSha256 !== undefined && !isSha256(value.layoutSha256)) {
        errors.push(`${path}.layoutSha256 must be SHA-256.`);
      }
      if (value.pagination !== "flow") errors.push(`${path}.pagination must be flow.`);
      break;
    case "fixed-page":
      validateOnlyKeys(errors, value, ["kind", "masterRef", "canvas"], path);
      if (!isNonEmptyString(value.masterRef)) errors.push(`${path}.masterRef is required.`);
      if (!( ["wide", "standard", "a4", "letter"] as unknown[]).includes(value.canvas)) {
        errors.push(`${path}.canvas is unsupported.`);
      }
      break;
    case "workbook":
      validateOnlyKeys(errors, value, ["kind", "workbookSidecarRef"], path);
      if (!isNonEmptyString(value.workbookSidecarRef)) {
        errors.push(`${path}.workbookSidecarRef is required.`);
      }
      break;
    case "block-page":
      validateOnlyKeys(errors, value, ["kind", "blockSchemaRef"], path);
      if (!isNonEmptyString(value.blockSchemaRef)) {
        errors.push(`${path}.blockSchemaRef is required.`);
      }
      break;
  }
}

function validateVariantSourcePolicy(
  errors: string[],
  value: unknown,
  path: string,
): void {
  if (!isRecord(value)) {
    errors.push(`${path} is required.`);
    return;
  }
  validateOnlyKeys(
    errors,
    value,
    ["canonicalModel", "editorAdapters", "adapterCanonicalAuthority"],
    path,
  );
  if (value.canonicalModel !== "cogo-document-template-family") {
    errors.push(`${path}.canonicalModel must be cogo-document-template-family.`);
  }
  validateStringList(errors, value.editorAdapters, `${path}.editorAdapters`, {
    minItems: 1,
    unique: true,
    allowed: EDITOR_ADAPTERS,
  });
  if (value.adapterCanonicalAuthority !== false) {
    errors.push(`${path}.adapterCanonicalAuthority must be false.`);
  }
}

function validateVariantSlotCoverage(
  errors: string[],
  variant: Record<string, unknown>,
  path: string,
  sharedSlots: Array<Record<string, unknown>>,
): void {
  const sharedSlotIds = new Set(
    sharedSlots
      .map((slot) => slot.id)
      .filter((id): id is string => typeof id === "string"),
  );
  const requiredSlotIds = sharedSlots
    .filter((slot) => slot.required === true && typeof slot.id === "string")
    .map((slot) => slot.id as string);

  const bindings = Array.isArray(variant.slotBindings)
    ? variant.slotBindings
    : [];
  if (!Array.isArray(variant.slotBindings)) {
    errors.push(`${path}.slotBindings must be an array.`);
  }
  const bindingIds: string[] = [];
  bindings.forEach((binding, index) => {
    const bindingPath = `${path}.slotBindings[${index}]`;
    if (!isRecord(binding)) {
      errors.push(`${bindingPath} must be an object.`);
      return;
    }
    validateOnlyKeys(errors, binding, ["slotId", "bindingRef"], bindingPath);
    if (!isKebabId(binding.slotId)) {
      errors.push(`${bindingPath}.slotId is invalid.`);
    } else {
      bindingIds.push(binding.slotId);
      if (!sharedSlotIds.has(binding.slotId)) {
        errors.push(`${bindingPath}.slotId does not reference a shared slot.`);
      }
    }
    if (!isNonEmptyString(binding.bindingRef)) {
      errors.push(`${bindingPath}.bindingRef is required.`);
    }
  });
  if (!isUnique(bindingIds)) errors.push(`${path}.slotBindings slot ids must be unique.`);

  const omissions = Array.isArray(variant.approvedOmissions)
    ? variant.approvedOmissions
    : [];
  if (!Array.isArray(variant.approvedOmissions)) {
    errors.push(`${path}.approvedOmissions must be an array.`);
  }
  const omissionIds: string[] = [];
  omissions.forEach((omission, index) => {
    const omissionPath = `${path}.approvedOmissions[${index}]`;
    if (!isRecord(omission)) {
      errors.push(`${omissionPath} must be an object.`);
      return;
    }
    validateOnlyKeys(
      errors,
      omission,
      ["slotId", "reason", "approvedByRef", "evidenceRef"],
      omissionPath,
    );
    if (!isKebabId(omission.slotId)) {
      errors.push(`${omissionPath}.slotId is invalid.`);
    } else {
      omissionIds.push(omission.slotId);
      if (!sharedSlotIds.has(omission.slotId)) {
        errors.push(`${omissionPath}.slotId does not reference a shared slot.`);
      }
    }
    for (const key of ["reason", "approvedByRef", "evidenceRef"] as const) {
      if (!isNonEmptyString(omission[key])) {
        errors.push(`${omissionPath}.${key} is required.`);
      }
    }
  });
  if (!isUnique(omissionIds)) {
    errors.push(`${path}.approvedOmissions slot ids must be unique.`);
  }
  for (const id of bindingIds) {
    if (omissionIds.includes(id)) {
      errors.push(`${path} slot ${id} cannot be both bound and omitted.`);
    }
  }
  for (const id of requiredSlotIds) {
    if (!bindingIds.includes(id) && !omissionIds.includes(id)) {
      errors.push(`${path} required shared slot ${id} must be bound or approved omitted.`);
    }
  }
}

function validateVariant(
  errors: string[],
  value: unknown,
  index: number,
  sharedSlots: Array<Record<string, unknown>>,
): void {
  const path = `variants[${index}]`;
  if (!isRecord(value)) {
    errors.push(`${path} must be an object.`);
    return;
  }
  validateOnlyKeys(
    errors,
    value,
    [
      "variantId",
      "name",
      "layoutProfile",
      "capabilityStatus",
      "knownLimitations",
      "layout",
      "sourcePolicy",
      "slotBindings",
      "approvedOmissions",
      "renderTargets",
      "publicationTargets",
      "targetOverlays",
    ],
    path,
  );
  if (!isKebabId(value.variantId)) errors.push(`${path}.variantId is invalid.`);
  if (!isNonEmptyString(value.name)) errors.push(`${path}.name is required.`);
  if (!COGO_DOCUMENT_TEMPLATE_LAYOUT_PROFILES.includes(
    value.layoutProfile as CogoDocumentTemplateLayoutProfile,
  )) {
    errors.push(`${path}.layoutProfile is unsupported.`);
    return;
  }
  const profile = value.layoutProfile as CogoDocumentTemplateLayoutProfile;
  validateVariantLayout(errors, value.layout, profile, `${path}.layout`);
  if (!CAPABILITY_STATUSES.includes(
    value.capabilityStatus as CogoDocumentTemplateCapabilityStatus,
  )) {
    errors.push(`${path}.capabilityStatus is unsupported.`);
  }
  validateStringList(errors, value.knownLimitations, `${path}.knownLimitations`, {
    unique: true,
  });
  validateVariantSourcePolicy(errors, value.sourcePolicy, `${path}.sourcePolicy`);
  validateVariantSlotCoverage(errors, value, path, sharedSlots);

  const renderTargets = isStringArray(value.renderTargets)
    ? value.renderTargets
    : [];
  const publicationTargets = isStringArray(value.publicationTargets)
    ? value.publicationTargets
    : [];
  if (!isStringArray(value.renderTargets)) {
    errors.push(`${path}.renderTargets must be an array.`);
  }
  if (!isStringArray(value.publicationTargets)) {
    errors.push(`${path}.publicationTargets must be an array.`);
  }
  if (!isUnique(renderTargets)) errors.push(`${path}.renderTargets must be unique.`);
  if (!isUnique(publicationTargets)) {
    errors.push(`${path}.publicationTargets must be unique.`);
  }
  if (renderTargets.length + publicationTargets.length === 0) {
    errors.push(`${path} must declare at least one target.`);
  }
  if (profile === "workbook" && !renderTargets.includes("xlsx")) {
    errors.push(`${path}.renderTargets must include xlsx for workbook.`);
  }

  const compatibility = COGO_DOCUMENT_TEMPLATE_PROFILE_TARGET_COMPATIBILITY[profile];
  for (const target of renderTargets) {
    if (!(compatibility.renderTargets as readonly string[]).includes(target)) {
      errors.push(`${path} target ${target} is incompatible with ${profile}.`);
    }
  }
  for (const target of publicationTargets) {
    if (!(compatibility.publicationTargets as readonly string[]).includes(target)) {
      errors.push(`${path} target ${target} is incompatible with ${profile}.`);
    }
  }

  if (!Array.isArray(value.targetOverlays)) {
    errors.push(`${path}.targetOverlays must be an array.`);
    return;
  }
  const overlayTargets = value.targetOverlays
    .filter(isRecord)
    .map((overlay) => overlay.target)
    .filter((target): target is string => typeof target === "string");
  if (!isUnique(overlayTargets)) {
    errors.push(`${path}.targetOverlays targets must be unique.`);
  }
  const declaredTargets = [...renderTargets, ...publicationTargets].sort();
  if (
    JSON.stringify([...overlayTargets].sort()) !== JSON.stringify(declaredTargets)
  ) {
    errors.push(`${path}.targetOverlays must exactly cover declared targets.`);
  }
  value.targetOverlays.forEach((overlay, overlayIndex) => {
    if (!isRecord(overlay)) {
      errors.push(`${path}.targetOverlays[${overlayIndex}] must be an object.`);
      return;
    }
    validateTargetOverlayPolicy(
      errors,
      overlay,
      `${path}.targetOverlays[${overlayIndex}]`,
    );
  });
  if (
    value.capabilityStatus === "production-ready" &&
    value.targetOverlays.some(
      (overlay) =>
        !isRecord(overlay) || overlay.implementationStatus !== "production-ready",
    )
  ) {
    errors.push(
      `${path}.capabilityStatus cannot be production-ready while any target overlay is not production-ready.`,
    );
  }
}

function validateMetadata(errors: string[], value: unknown): void {
  const path = "metadata";
  if (!isRecord(value)) {
    errors.push(`${path} is required.`);
    return;
  }
  validateOnlyKeys(errors, value, ["ownerRef", "locale", "tags"], path);
  if (!isNonEmptyString(value.ownerRef)) errors.push(`${path}.ownerRef is required.`);
  if (
    typeof value.locale !== "string" ||
    !/^[a-z]{2,3}(?:-[A-Z]{2})?$/.test(value.locale)
  ) {
    errors.push(`${path}.locale is invalid.`);
  }
  validateStringList(errors, value.tags, `${path}.tags`, {
    unique: true,
    kebab: true,
  });
}

function validateLegacyTemplateRefs(errors: string[], value: unknown): void {
  if (!Array.isArray(value)) {
    errors.push("legacyTemplateRefs must be an array.");
    return;
  }
  value.forEach((entry, index) => {
    const path = `legacyTemplateRefs[${index}]`;
    if (!isRecord(entry)) {
      errors.push(`${path} must be an object.`);
      return;
    }
    validateOnlyKeys(
      errors,
      entry,
      [
        "schemaVersion",
        "templateId",
        "templateVersion",
        "manifestRef",
        "manifestSha256",
      ],
      path,
    );
    if (entry.schemaVersion !== "cogo.document-template.v1") {
      errors.push(`${path}.schemaVersion must be cogo.document-template.v1.`);
    }
    if (!isKebabId(entry.templateId)) errors.push(`${path}.templateId is invalid.`);
    if (
      typeof entry.templateVersion !== "string" ||
      !/^\d+\.\d+\.\d+$/.test(entry.templateVersion)
    ) {
      errors.push(`${path}.templateVersion is invalid.`);
    }
    if (!isNonEmptyString(entry.manifestRef)) {
      errors.push(`${path}.manifestRef is required.`);
    }
    if (!isSha256(entry.manifestSha256)) {
      errors.push(`${path}.manifestSha256 must be SHA-256.`);
    }
  });
}

function validateSemanticSlot(
  errors: string[],
  value: unknown,
  index: number,
): Record<string, unknown> | null {
  const path = `semanticCore.slots[${index}]`;
  if (!isRecord(value)) {
    errors.push(`${path} must be an object.`);
    return null;
  }
  const referenced = "slotRef" in value;
  validateOnlyKeys(
    errors,
    value,
    referenced
      ? ["id", "slotRef", "kind", "required", "semanticRole"]
      : ["id", "kind", "bindingPath", "required", "semanticRole", "shapeRef"],
    path,
  );
  if (!isKebabId(value.id)) errors.push(`${path}.id is invalid.`);
  if (!SEMANTIC_SLOT_KINDS.includes(
    value.kind as CogoDocumentTemplateSemanticSlotKind,
  )) {
    errors.push(`${path}.kind is unsupported.`);
  }
  if (typeof value.required !== "boolean") {
    errors.push(`${path}.required must be boolean.`);
  }
  if (!isKebabId(value.semanticRole)) {
    errors.push(`${path}.semanticRole is invalid.`);
  }
  if (referenced) {
    if (!isNonEmptyString(value.slotRef)) errors.push(`${path}.slotRef is required.`);
  } else {
    if (
      typeof value.bindingPath !== "string" ||
      !/^\/(?:[^/~]|~[01])+(?:\/(?:[^/~]|~[01])+)*$/.test(value.bindingPath)
    ) {
      errors.push(`${path}.bindingPath must be a JSON pointer.`);
    }
    if (value.shapeRef !== undefined && !isNonEmptyString(value.shapeRef)) {
      errors.push(`${path}.shapeRef must be non-empty when present.`);
    }
  }
  return value;
}

function validateSemanticCore(
  errors: string[],
  value: unknown,
): Array<Record<string, unknown>> {
  const path = "semanticCore";
  if (!isRecord(value)) {
    errors.push(`${path} is required.`);
    return [];
  }
  validateOnlyKeys(
    errors,
    value,
    [
      "dataSchema",
      "slots",
      "tokens",
      "assets",
      "guidelineRefs",
      "llmPolicy",
      "actionFlowRefs",
      "proofProfile",
      "sourceProvenance",
    ],
    path,
  );

  if (!isRecord(value.dataSchema)) {
    errors.push(`${path}.dataSchema is required.`);
  } else {
    validateOnlyKeys(errors, value.dataSchema, ["ref", "sha256"], `${path}.dataSchema`);
    if (!isNonEmptyString(value.dataSchema.ref)) {
      errors.push(`${path}.dataSchema.ref is required.`);
    }
    validateOptionalSha256(
      errors,
      value.dataSchema.sha256,
      `${path}.dataSchema.sha256`,
    );
  }

  const slots: Array<Record<string, unknown>> = [];
  if (!Array.isArray(value.slots) || value.slots.length === 0) {
    errors.push(`${path}.slots must be non-empty.`);
  } else {
    value.slots.forEach((slot, index) => {
      const admitted = validateSemanticSlot(errors, slot, index);
      if (admitted) slots.push(admitted);
    });
    pushDuplicateIdError(errors, value.slots, "id", "Semantic slot");
  }

  if (!isRecord(value.tokens)) {
    errors.push(`${path}.tokens is required.`);
  } else {
    validateOnlyKeys(errors, value.tokens, ["designTokenRefs"], `${path}.tokens`);
    validateStringList(
      errors,
      value.tokens.designTokenRefs,
      `${path}.tokens.designTokenRefs`,
      { minItems: 1, unique: true },
    );
  }

  if (!Array.isArray(value.assets)) {
    errors.push(`${path}.assets must be an array.`);
  } else {
    value.assets.forEach((asset, index) => {
      const assetPath = `${path}.assets[${index}]`;
      if (!isRecord(asset)) {
        errors.push(`${assetPath} must be an object.`);
        return;
      }
      validateOnlyKeys(
        errors,
        asset,
        ["id", "role", "sourceRef", "mediaType", "sha256"],
        assetPath,
      );
      if (!isKebabId(asset.id)) errors.push(`${assetPath}.id is invalid.`);
      if (!isKebabId(asset.role)) errors.push(`${assetPath}.role is invalid.`);
      if (!isNonEmptyString(asset.sourceRef)) errors.push(`${assetPath}.sourceRef is required.`);
      if (!isNonEmptyString(asset.mediaType)) errors.push(`${assetPath}.mediaType is required.`);
      validateOptionalSha256(errors, asset.sha256, `${assetPath}.sha256`);
    });
    pushDuplicateIdError(errors, value.assets, "id", "Asset");
  }

  if (!Array.isArray(value.guidelineRefs) || value.guidelineRefs.length === 0) {
    errors.push(`${path}.guidelineRefs must be non-empty.`);
  } else {
    value.guidelineRefs.forEach((guideline, index) => {
      const guidelinePath = `${path}.guidelineRefs[${index}]`;
      if (!isRecord(guideline)) {
        errors.push(`${guidelinePath} must be an object.`);
        return;
      }
      validateOnlyKeys(errors, guideline, ["ref", "required", "sha256"], guidelinePath);
      if (!isNonEmptyString(guideline.ref)) errors.push(`${guidelinePath}.ref is required.`);
      if (typeof guideline.required !== "boolean") {
        errors.push(`${guidelinePath}.required must be boolean.`);
      }
      validateOptionalSha256(errors, guideline.sha256, `${guidelinePath}.sha256`);
    });
  }

  if (!isRecord(value.llmPolicy)) {
    errors.push(`${path}.llmPolicy is required.`);
  } else {
    validateOnlyKeys(
      errors,
      value.llmPolicy,
      [
        "allowedRoles",
        "canonicalAuthority",
        "finalWriterAllowed",
        "outputRequiresValidation",
      ],
      `${path}.llmPolicy`,
    );
    validateStringList(
      errors,
      value.llmPolicy.allowedRoles,
      `${path}.llmPolicy.allowedRoles`,
      { minItems: 1, unique: true, allowed: LLM_ROLES },
    );
    validateRequiredPolicy(
      errors,
      value.llmPolicy,
      {
        canonicalAuthority: false,
        finalWriterAllowed: false,
        outputRequiresValidation: true,
      },
      `${path}.llmPolicy`,
    );
  }

  if (!Array.isArray(value.actionFlowRefs) || value.actionFlowRefs.length === 0) {
    errors.push(`${path}.actionFlowRefs must be non-empty.`);
  } else {
    value.actionFlowRefs.forEach((actionFlow, index) => {
      const actionPath = `${path}.actionFlowRefs[${index}]`;
      if (!isRecord(actionFlow)) {
        errors.push(`${actionPath} must be an object.`);
        return;
      }
      validateOnlyKeys(errors, actionFlow, ["id", "ref", "role", "sha256"], actionPath);
      if (!isKebabId(actionFlow.id)) errors.push(`${actionPath}.id is invalid.`);
      if (!isNonEmptyString(actionFlow.ref)) errors.push(`${actionPath}.ref is required.`);
      if (!ACTION_FLOW_ROLES.includes(actionFlow.role as (typeof ACTION_FLOW_ROLES)[number])) {
        errors.push(`${actionPath}.role is unsupported.`);
      }
      validateOptionalSha256(errors, actionFlow.sha256, `${actionPath}.sha256`);
    });
    pushDuplicateIdError(errors, value.actionFlowRefs, "id", "ActionFlow ref");
  }

  if (!isRecord(value.proofProfile)) {
    errors.push(`${path}.proofProfile is required.`);
  } else {
    validateOnlyKeys(
      errors,
      value.proofProfile,
      [
        "requiredForPromotion",
        "nativeStructureReadbackRequired",
        "targetReadbackRequired",
        "structureHashRequired",
        "providerVersionPolicy",
        "visualDiffPolicy",
        "evidenceRetention",
      ],
      `${path}.proofProfile`,
    );
    validateRequiredPolicy(
      errors,
      value.proofProfile,
      {
        requiredForPromotion: true,
        nativeStructureReadbackRequired: true,
        targetReadbackRequired: true,
        structureHashRequired: true,
        providerVersionPolicy: "required-when-provider",
        evidenceRetention: "immutable",
      },
      `${path}.proofProfile`,
    );
    if (
      value.proofProfile.visualDiffPolicy !== "always" &&
      value.proofProfile.visualDiffPolicy !== "when-layout-bearing"
    ) {
      errors.push(`${path}.proofProfile.visualDiffPolicy is unsupported.`);
    }
  }

  if (!isRecord(value.sourceProvenance)) {
    errors.push(`${path}.sourceProvenance is required.`);
  } else {
    validateOnlyKeys(
      errors,
      value.sourceProvenance,
      [
        "required",
        "sourceRefs",
        "preserveProviderVersion",
        "sourceAdaptersCanonical",
      ],
      `${path}.sourceProvenance`,
    );
    validateStringList(
      errors,
      value.sourceProvenance.sourceRefs,
      `${path}.sourceProvenance.sourceRefs`,
      { minItems: 1, unique: true },
    );
    validateRequiredPolicy(
      errors,
      value.sourceProvenance,
      {
        required: true,
        preserveProviderVersion: true,
        sourceAdaptersCanonical: false,
      },
      `${path}.sourceProvenance`,
    );
  }
  return slots;
}

function validatePromotionPolicy(errors: string[], value: unknown): void {
  if (!isRecord(value)) {
    errors.push("promotionPolicy is required.");
    return;
  }
  validateOnlyKeys(
    errors,
    value,
    [
      "humanPromotionRequired",
      "automatedPromotionAllowed",
      "modelMayApprove",
      "evidenceRequired",
    ],
    "promotionPolicy",
  );
  validateRequiredPolicy(
    errors,
    value,
    {
      humanPromotionRequired: true,
      automatedPromotionAllowed: false,
      modelMayApprove: false,
      evidenceRequired: true,
    },
    "promotionPolicy",
  );
}

export function validateDocumentTemplateFamily(value: unknown): string[] {
  if (!isRecord(value)) return ["Document template family must be an object."];
  const errors: string[] = [];
  validateOnlyKeys(
    errors,
    value,
    [
      "schemaVersion",
      "familyId",
      "familyVersion",
      "name",
      "description",
      "metadata",
      "legacyTemplateRefs",
      "semanticCore",
      "promotionPolicy",
      "variants",
    ],
    "documentTemplateFamily",
  );
  if (value.schemaVersion !== COGO_DOCUMENT_TEMPLATE_FAMILY_SCHEMA_VERSION) {
    errors.push("Unsupported document template family schemaVersion.");
  }
  if (!isKebabId(value.familyId)) errors.push("familyId must be kebab-case.");
  if (
    typeof value.familyVersion !== "string" ||
    !/^\d+\.\d+\.\d+$/.test(value.familyVersion)
  ) {
    errors.push("familyVersion must be semantic version x.y.z.");
  }
  if (!isNonEmptyString(value.name)) errors.push("name is required.");
  if (!isNonEmptyString(value.description)) errors.push("description is required.");
  validateMetadata(errors, value.metadata);
  validateLegacyTemplateRefs(errors, value.legacyTemplateRefs);
  const sharedSlots = validateSemanticCore(errors, value.semanticCore);
  validatePromotionPolicy(errors, value.promotionPolicy);

  if (!Array.isArray(value.variants) || value.variants.length === 0) {
    errors.push("variants must be non-empty.");
  } else {
    pushDuplicateIdError(errors, value.variants, "variantId", "Variant");
    value.variants.forEach((variant, index) =>
      validateVariant(errors, variant, index, sharedSlots),
    );
  }
  return [...new Set(errors)];
}

/**
 * Semantic-runtime assertion only. Canonical ingestion must use
 * assertDocumentTemplateFamilyAdmission so the JSON Schema and this validator
 * both execute. This helper remains for already-loaded, package-owned fixtures.
 */
export function assertDocumentTemplateFamily(
  value: unknown,
): asserts value is CogoDocumentTemplateFamily {
  const errors = validateDocumentTemplateFamily(value);
  if (errors.length > 0) {
    throw new Error(`Invalid document template family: ${errors.join(" ")}`);
  }
}
